import { useState } from 'react';
import { Link, NavLink, Outlet } from 'react-router-dom';
import { Menu, X, Heart, Phone, MapPin, Calendar, Activity, Stethoscope, ChevronRight } from 'lucide-react';

const navLinks = [
  { to: '/clinics', label: 'Find Clinics', icon: MapPin },
  { to: '/services', label: 'Services', icon: Activity },
  { to: '/triage', label: 'Symptom Checker', icon: Stethoscope },
];

export default function Layout() {
  const [mobileOpen, setMobileOpen] = useState(false);

  return (
    <div className="min-h-screen flex flex-col pb-12">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-white border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5" onClick={() => setMobileOpen(false)}>
            <span className="w-9 h-9 bg-accent rounded-lg flex items-center justify-center">
              <Heart className="w-5 h-5 text-white fill-white" />
            </span>
            <span className="text-navy font-extrabold tracking-tight text-lg uppercase">
              Clinic Connect
            </span>
          </Link>

          {/* Desktop nav */}
          <nav className="hidden md:flex items-center gap-1">
            {navLinks.map((link) => (
              <NavLink
                key={link.to}
                to={link.to}
                className={({ isActive }) =>
                  `px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    isActive
                      ? 'text-primary bg-primary-50'
                      : 'text-gray-600 hover:text-primary hover:bg-gray-50'
                  }`
                }
              >
                {link.label}
              </NavLink>
            ))}
            <Link to="/clinics" className="btn btn-primary btn-sm ml-2">
              <Calendar className="w-4 h-4" />
              Book Appointment
            </Link>
          </nav>

          {/* Mobile hamburger */}
          <button
            className="md:hidden p-2 text-navy"
            onClick={() => setMobileOpen(!mobileOpen)}
            aria-label="Toggle menu"
          >
            {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile drawer */}
        {mobileOpen && (
          <div className="md:hidden border-t border-gray-100 bg-white">
            <nav className="flex flex-col p-4 gap-1">
              {navLinks.map((link) => (
                <NavLink
                  key={link.to}
                  to={link.to}
                  onClick={() => setMobileOpen(false)}
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-colors ${
                      isActive
                        ? 'text-primary bg-primary-50'
                        : 'text-gray-600 hover:text-primary hover:bg-gray-50'
                    }`
                  }
                >
                  <link.icon className="w-5 h-5" />
                  {link.label}
                  <ChevronRight className="w-4 h-4 ml-auto opacity-40" />
                </NavLink>
              ))}
              <Link
                to="/clinics"
                onClick={() => setMobileOpen(false)}
                className="btn btn-primary mt-2"
              >
                <Calendar className="w-4 h-4" />
                Book Appointment
              </Link>
            </nav>
          </div>
        )}
      </header>

      {/* Main */}
      <main className="flex-1">
        <Outlet />
      </main>

      {/* Footer */}
      <footer className="bg-navy text-white mt-16">
        <div className="max-w-7xl mx-auto px-4 py-12">
          <div className="grid md:grid-cols-3 gap-8">
            <div>
              <div className="flex items-center gap-2.5 mb-4">
                <span className="w-8 h-8 bg-accent rounded-lg flex items-center justify-center">
                  <Heart className="w-4 h-4 text-white fill-white" />
                </span>
                <span className="font-extrabold tracking-tight uppercase">Clinic Connect</span>
              </div>
              <p className="text-gray-400 text-sm leading-relaxed">
                The Ministry of Health &amp; Wellness digital healthcare platform connecting
                every Bahamian to comprehensive healthcare services.
              </p>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Quick Links</h4>
              <ul className="space-y-2 text-sm text-gray-400">
                <li><Link to="/clinics" className="hover:text-white transition-colors">Find Clinics</Link></li>
                <li><Link to="/services" className="hover:text-white transition-colors">Services</Link></li>
                <li><Link to="/triage" className="hover:text-white transition-colors">Symptom Checker</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Emergency</h4>
              <ul className="space-y-2 text-sm text-gray-400">
                <li>Emergency: <a href="tel:911" className="text-white font-semibold">911</a></li>
                <li>Health Hotline: <a href="tel:919" className="text-white font-semibold">919</a></li>
                <li>PMH: <a href="tel:+12423222861" className="text-white font-semibold">(242) 322-2861</a></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-white/10 mt-8 pt-8 text-center text-xs text-gray-500">
            &copy; 2026 High Functioning Solutions Ltd. &middot; Ministry of Health &amp; Wellness
          </div>
        </div>
      </footer>

      {/* Emergency Bar — fixed bottom */}
      <div className="fixed bottom-0 left-0 right-0 z-50 bg-danger text-white">
        <div className="max-w-7xl mx-auto px-4 py-2.5 flex items-center justify-center gap-2 text-xs sm:text-sm font-semibold tracking-wide">
          <Phone className="w-4 h-4 flex-shrink-0" />
          <span>
            EMERGENCY? CALL{' '}
            <a href="tel:911" className="underline font-bold">911</a>
            {' '}OR{' '}
            <a href="tel:919" className="underline font-bold">919</a>
            {' '}INSTANTLY
          </span>
        </div>
      </div>
    </div>
  );
}
